import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@/components/ui/Icon";
import { WorkspaceBadge } from "@/components/ui/WorkspaceBadge";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { TicketDetailOverlay } from "@/components/board/TicketDetailOverlay";
import { SubmitPickerSection } from "@/components/dashboard/SubmitPickerSection";
import { IdeaPickerSection } from "@/components/dashboard/IdeaPickerSection";
import { HomeCalendarWidget } from "@/components/dashboard/HomeCalendarWidget";
import { TicketAgeChip, TicketDueChip, TicketIdChip } from "@/components/board/TicketChips";
import { ticketLabel } from "@/lib/boardView";
import { CreateWorkspaceDialog } from "@/components/dialogs/CreateWorkspaceDialog";
import { CreateBoardDialog } from "@/components/dialogs/CreateBoardDialog";
import { statusColorOf } from "@/lib/tokens";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { resolveWorkspaceColor } from "@/lib/colors";
import { compareBoardOrder } from "@/lib/boardOrder";
import { BoardListRow } from "@/components/board/BoardListRow";
import { t } from "@/lib/strings";
import { useAuth } from "@/hooks/useAuth";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useMyBoards } from "@/hooks/useMyBoards";
import type {
  Board,
  MySubmissionItem,
  OpenWorkItem,
  SharedTicketItem,
  UUID,
  Workspace,
} from "@/lib/types";

function greetingFor(name: string): string {
  const h = new Date().getHours();
  const greet = h < 11 ? t.greetingMorning : h < 18 ? t.greetingDay : t.greetingEvening;
  return name ? `${greet}, ${name}.` : `${greet}.`;
}

const HOME_CAL_KEY = "flow:home-calendar";

// Mon..Fri (local) of the current week as YYYY-MM-DD, for the Home calendar feed.
function mondayToFriday(): { start: string; end: string } {
  const pad = (n: number) => String(n).padStart(2, "0");
  const key = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const now = new Date();
  const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
  const fri = new Date(mon);
  fri.setDate(mon.getDate() + 4);
  return { start: key(mon), end: key(fri) };
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Only flow-admins may create workspaces (= company departments). Everyone else
  // uses the shared "Persönlich" workspace for private boards. Backend enforces too.
  const canCreateWorkspace = user?.roles.includes("flow-admin") ?? false;
  // F7: the Home calendar widget is opt-in (default off), remembered per browser.
  const [showCalendar, setShowCalendar] = useState(() => {
    try {
      return localStorage.getItem(HOME_CAL_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleCalendar = () =>
    setShowCalendar((v) => {
      const next = !v;
      try {
        localStorage.setItem(HOME_CAL_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  const isDesktop = useBreakpoint("md");
  const [createWsOpen, setCreateWsOpen] = useState(false);
  const [createBoardCtx, setCreateBoardCtx] = useState<string | null>(null);
  // Open a ticket's detail as an overlay on top of Home (no navigation away).
  const [openTicket, setOpenTicket] = useState<{ boardId: UUID; rowId: UUID } | null>(null);

  const wsQuery = useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: api.listWorkspaces,
  });
  const recentBoards = useMyBoards(6);
  const mySubmissions = useQuery({
    queryKey: queryKeys.mySubmissions,
    queryFn: () => api.listMySubmissions(),
  });
  const openWorkQuery = useQuery({
    queryKey: ["my-open-work"],
    queryFn: () => api.listOpenWork(),
  });
  const sharedTicketsQuery = useQuery({
    queryKey: queryKeys.sharedTickets,
    queryFn: () => api.getSharedTickets(),
  });
  // F7: the Home calendar feed (Mon–Fri of the current week) — only fetched while
  // the widget is shown. Dated rows across the user's boards (not just assigned).
  const calWeek = useMemo(() => mondayToFriday(), []);
  const calendarQuery = useQuery({
    queryKey: ["home-calendar", calWeek.start, calWeek.end],
    queryFn: () => api.listHomeCalendar(calWeek.start, calWeek.end),
    enabled: showCalendar,
  });

  const workspaces: Workspace[] = wsQuery.data ?? [];
  const boards: Board[] = recentBoards.data ?? [];
  const submissions: MySubmissionItem[] = mySubmissions.data ?? [];
  const sharedTickets: SharedTicketItem[] = sharedTicketsQuery.data ?? [];
  const openWork: OpenWorkItem[] = openWorkQuery.data ?? [];
  // Backend already orders deadline-first, so a plain split preserves order.
  // Deadline-less work (the "assign everything to myself" pile) is tucked into a
  // collapsed group; it auto-opens when there's little deadline work to show.
  const openWithDeadline = openWork.filter((o) => o.due);
  const openNoDeadline = openWork.filter((o) => !o.due);
  const [showNoDeadline, setShowNoDeadline] = useState(false);
  const noDeadlineVisible = openWithDeadline.length < 5 || showNoDeadline;
  const toWorkItems = (list: OpenWorkItem[]) =>
    list.map((o) => ({
      key: o.row_id,
      boardId: o.board_id,
      rowId: o.row_id,
      title: o.title,
      boardName: o.board_name,
      workspaceName: o.workspace_name,
      workspaceIcon: o.workspace_icon,
      workspaceColor: o.workspace_color,
      workspaceId: o.workspace_id,
      statusLabel: o.status_label,
      statusColor: o.status_color,
      chip: o.kind === "ticket" ? t.chipTicket : t.chipTask,
      idLabel: ticketLabel(o.ticket_prefix, o.ticket_number),
      // Age/lifetime is a ticket concept — assigned tasks don't get it.
      createdAt: o.kind === "ticket" ? o.created_at : null,
      resolvedAt: o.kind === "ticket" ? o.resolved_at : null,
      due: o.due,
    }));

  const displayName = user?.name?.split(" ")[0] ?? "";

  return (
    <div
      style={{
        flex: 1,
        overflow: "auto",
        padding: isDesktop ? "28px 36px 80px" : "16px 12px 80px",
        background: "var(--bg)",
      }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 28 }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {new Date().toLocaleDateString("de-DE", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </div>
            <h1
              style={{
                margin: "6px 0 4px",
                fontSize: 28,
                fontWeight: 600,
                letterSpacing: "-0.015em",
                color: "var(--ink-1)",
              }}
            >
              {greetingFor(displayName)}
            </h1>
          </div>
          <Button
            variant="ghost"
            size="md"
            icon="date"
            onClick={toggleCalendar}
            title={showCalendar ? t.homeCalendarHide : t.homeCalendarShow}
          >
            {showCalendar ? t.homeCalendarHide : t.homeCalendarShow}
          </Button>
          {canCreateWorkspace ? (
            <Button variant="primary" size="md" icon="plus" onClick={() => setCreateWsOpen(true)}>
              {t.newWorkspace}
            </Button>
          ) : null}
        </div>

        {/* F7: opt-in one-work-week calendar of the user's dated open work. */}
        {showCalendar ? (
          <>
            <SectionHd title={t.homeCalendarTitle} sub={t.homeCalendarSub} />
            <HomeCalendarWidget
              items={calendarQuery.data ?? []}
              onOpen={(boardId, rowId) => setOpenTicket({ boardId, rowId })}
            />
          </>
        ) : null}

        {/* Anfrage stellen — the submit picker, promoted to the top. */}
        <SubmitPickerSection />

        {/* Idee einreichen — company-wide Ideenbox(en) published to Home. */}
        <IdeaPickerSection />

        {/* Offene Tickets — your to-do queue (inbound + assigned). Above
            Meine Tickets: work you owe is more important than requests you sent. */}
        <SectionHd title={t.homeOpenTickets} sub={t.homeOpenTicketsSub} />
        {openWithDeadline.length > 0 || openWork.length === 0 ? (
          <TicketList
            loading={openWorkQuery.isLoading}
            emptyText={t.homeNoOpenWork}
            items={toWorkItems(openWithDeadline)}
            onOpen={(boardId, rowId) => setOpenTicket({ boardId, rowId })}
          />
        ) : null}
        {openNoDeadline.length > 0 ? (
          <div style={{ marginBottom: 32 }}>
            <button
              type="button"
              onClick={() => setShowNoDeadline((v) => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 2px 8px",
                fontSize: 12,
                fontWeight: 600,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              <Icon
                name={noDeadlineVisible ? "chevDown" : "chevRight"}
                size={12}
                color="var(--ink-3)"
              />
              {t.homeNoDeadlineGroup} · {openNoDeadline.length}
            </button>
            {noDeadlineVisible ? (
              <TicketList
                loading={false}
                emptyText=""
                items={toWorkItems(openNoDeadline)}
                onOpen={(boardId, rowId) => setOpenTicket({ boardId, rowId })}
              />
            ) : null}
          </div>
        ) : null}

        {/* Meine Tickets — deine Tickets an andere Abteilungen. */}
        <SectionHd title={t.homeMyTickets} sub={t.homeMyTicketsSub} />
        <TicketList
          loading={mySubmissions.isLoading}
          emptyText={t.homeNoMyTickets}
          items={submissions.map((s) => ({
            key: s.row_id,
            boardId: s.board_id,
            rowId: s.row_id,
            title: s.title,
            boardName: s.board_name,
            statusLabel: s.current_status_label,
            statusColor: s.current_status_color,
            chip: null,
            idLabel: ticketLabel(s.ticket_prefix, s.ticket_number),
            createdAt: s.board_kind === "tickets" ? s.created_at : null,
            resolvedAt: s.board_kind === "tickets" ? s.resolved_at : null,
          }))}
          onOpen={(boardId, rowId) => setOpenTicket({ boardId, rowId })}
        />

        {/* Mit dir geteilt — tickets others shared with you or your department. */}
        {sharedTickets.length > 0 ? (
          <>
            <SectionHd title={t.sharedWithYou} sub={t.sharedWithYouSub} />
            <TicketList
              loading={sharedTicketsQuery.isLoading}
              emptyText=""
              items={sharedTickets.map((s) => ({
                key: s.row_id,
                boardId: s.board_id,
                rowId: s.row_id,
                title: s.title,
                boardName: s.board_name,
                workspaceId: s.workspace_id,
                workspaceName: s.workspace_name,
                workspaceIcon: s.workspace_icon,
                workspaceColor: s.workspace_color,
                statusLabel: s.status_label,
                statusColor: s.status_color,
                chip:
                  s.share_origin === "department"
                    ? `${t.sharedChipDept} ${s.department_name ?? ""}`.trim()
                    : t.sharedChipPerson,
                idLabel: ticketLabel(s.ticket_prefix, s.ticket_number),
                createdAt: s.board_kind === "tickets" ? s.created_at : null,
                resolvedAt: s.board_kind === "tickets" ? s.resolved_at : null,
              }))}
              onOpen={(boardId, rowId) => setOpenTicket({ boardId, rowId })}
            />
          </>
        ) : null}

        <SectionHd title={t.homeMyBoards} />
        {recentBoards.isLoading ? (
          <SkeletonGrid />
        ) : boards.length === 0 ? (
          <EmptyState
            title={t.emptyRecentBoardsTitle}
            hint={t.emptyRecentBoardsHint}
            cta={
              workspaces.length === 0 && canCreateWorkspace ? (
                <Button
                  variant="primary"
                  size="md"
                  icon="plus"
                  onClick={() => setCreateWsOpen(true)}
                >
                  {t.newWorkspace}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="md"
                  icon="plus"
                  onClick={() => {
                    const first = workspaces[0];
                    if (first) setCreateBoardCtx(first.id);
                  }}
                >
                  {t.newBoard}
                </Button>
              )
            }
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 36 }}>
            {[...boards].sort(compareBoardOrder).map((b) => (
              <BoardListRow
                key={b.id}
                board={b}
                onClick={() => navigate({ to: "/b/$boardId", params: { boardId: b.id } })}
              />
            ))}
          </div>
        )}

        <SectionHd title={t.yourWorkspaces} />
        {wsQuery.isLoading ? (
          <SkeletonRows />
        ) : workspaces.length === 0 ? (
          <EmptyState
            title={t.emptyWorkspacesTitle}
            hint={t.emptyWorkspacesHint}
            cta={
              canCreateWorkspace ? (
                <Button
                  variant="primary"
                  size="md"
                  icon="plus"
                  onClick={() => setCreateWsOpen(true)}
                >
                  {t.newWorkspace}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div
            style={{
              background: "var(--surface)",
              borderRadius: 10,
              border: "1px solid var(--hairline)",
              overflow: "hidden",
            }}
          >
            {workspaces.map((ws, i) => {
              const color = resolveWorkspaceColor(ws);
              const boardCount = boards.filter((b) => b.workspace_id === ws.id).length;
              return (
                <Link
                  key={ws.id}
                  to="/w/$workspaceId"
                  params={{ workspaceId: ws.id }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 18px",
                    cursor: "pointer",
                    borderTop: i === 0 ? "none" : "1px solid var(--hairline-soft)",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface)")}
                >
                  <WorkspaceBadge name={ws.name} icon={ws.icon} color={color} size={36} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "var(--ink-1)" }}>
                      {ws.name}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                      {boardCount > 0
                        ? `${boardCount} ${boardCount === 1 ? "Board" : t.boardCount}`
                        : "Noch keine Boards"}
                    </div>
                  </div>
                  <Icon name="chevRight" size={14} color="var(--ink-4)" />
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <CreateWorkspaceDialog
        open={createWsOpen}
        onOpenChange={setCreateWsOpen}
        onCreated={(wsId) => navigate({ to: "/w/$workspaceId", params: { workspaceId: wsId } })}
      />
      {createBoardCtx ? (
        <CreateBoardDialog
          open={Boolean(createBoardCtx)}
          onOpenChange={(open) => !open && setCreateBoardCtx(null)}
          workspaceId={createBoardCtx}
          isPersonal={workspaces.find((w) => w.id === createBoardCtx)?.is_personal ?? false}
        />
      ) : null}
      {openTicket ? (
        <TicketDetailOverlay
          boardId={openTicket.boardId}
          rowId={openTicket.rowId}
          onClose={() => setOpenTicket(null)}
        />
      ) : null}
    </div>
  );
}

interface TicketRowData {
  key: string;
  boardId: UUID;
  rowId: UUID;
  title: string;
  boardName: string;
  workspaceId?: UUID | null;
  workspaceName?: string | null;
  workspaceIcon?: string | null;
  workspaceColor?: string | null;
  statusLabel: string | null;
  statusColor: string | null;
  chip: string | null;
  idLabel?: string | null;
  createdAt?: string | null;
  resolvedAt?: string | null;
  due?: string | null;
}

function TicketList({
  loading,
  emptyText,
  items,
  onOpen,
}: {
  loading: boolean;
  emptyText: string;
  items: TicketRowData[];
  onOpen: (boardId: UUID, rowId: UUID) => void;
}) {
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        borderRadius: 10,
        overflow: "hidden",
        marginBottom: 32,
      }}
    >
      {loading ? (
        <div style={{ padding: 20, opacity: 0.5, fontSize: 13 }}>{t.loading}</div>
      ) : items.length === 0 ? (
        <div
          style={{
            padding: "26px 16px",
            textAlign: "center",
            fontSize: 12.5,
            color: "var(--ink-4)",
          }}
        >
          {emptyText}
        </div>
      ) : (
        <div style={{ maxHeight: 360, overflowY: "auto" }}>
          {items.map((it, i) => {
            const tint = it.statusColor ? statusColorOf(it.statusColor) : null;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => onOpen(it.boardId, it.rowId)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "10px 14px",
                  textAlign: "left",
                  border: "none",
                  borderTop: i === 0 ? "none" : "1px solid var(--hairline-soft)",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {it.workspaceName ? (
                  <WorkspaceBadge
                    name={it.workspaceName}
                    icon={it.workspaceIcon}
                    color={resolveWorkspaceColor({
                      id: it.workspaceId ?? it.boardId,
                      color: it.workspaceColor ?? null,
                    })}
                    size={26}
                  />
                ) : null}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--ink-1)",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {it.title}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      color: "var(--ink-3)",
                      marginTop: 2,
                    }}
                  >
                    {it.idLabel ? <TicketIdChip label={it.idLabel} fontSize={11} /> : null}
                    <span
                      style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {it.workspaceName ? `${it.workspaceName} · ${it.boardName}` : it.boardName}
                    </span>
                  </div>
                </div>
                <TicketDueChip due={it.due} />
                <TicketAgeChip createdAt={it.createdAt} resolvedAt={it.resolvedAt} />
                {it.chip ? (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "var(--ink-3)",
                      background: "var(--surface-soft)",
                      border: "1px solid var(--hairline)",
                      borderRadius: 5,
                      padding: "1px 6px",
                      flexShrink: 0,
                    }}
                  >
                    {it.chip}
                  </span>
                ) : null}
                {it.statusLabel && tint ? (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: tint.fg,
                      background: tint.bg,
                      borderRadius: 999,
                      padding: "2px 9px",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {it.statusLabel}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SectionHd({ title, sub }: { title: string; sub?: string }) {
  return (
    <div style={{ marginBottom: 12, marginTop: 4, padding: "0 2px" }}>
      <h2
        style={{
          margin: 0,
          fontSize: 14,
          fontWeight: 600,
          color: "var(--ink-1)",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h2>
      {sub ? <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

function EmptyState({ title, hint, cta }: { title: string; hint: string; cta?: ReactNode }) {
  return (
    <div
      style={{
        background: "var(--surface)",
        borderRadius: 10,
        border: "1px dashed var(--hairline-strong)",
        padding: "40px 24px",
        textAlign: "center",
        marginBottom: 36,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 500, color: "var(--ink-1)", marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: "var(--ink-3)", marginBottom: cta ? 16 : 0 }}>{hint}</div>
      {cta}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 14,
        marginBottom: 36,
      }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 130,
            background: "var(--surface)",
            border: "1px solid var(--hairline)",
            borderRadius: 10,
            opacity: 0.55,
          }}
        />
      ))}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        borderRadius: 10,
        height: 64 * 3,
        opacity: 0.55,
      }}
    />
  );
}

// Surface Avatar so this module owns the entire dashboard render.
export { Avatar };
